<template>
  <x-form :submit-text="$t('Invia')" :submit-help="`* ${$t('Campi obbligatorii')}`" @submit="send" style="max-width: 80ch">
    <pf-title h="4">{{ $t('Dati del registrante del dominio {0}', [transfer.dominio]) }}</pf-title>
    <input type="hidden" name="id" :value="domainData.id" />

    <form-group :name="$t('Questo dominio è?')" required>
      <pf-radio
        :checked="domainData.new_registration === true"
        @change="domainData.new_registration = true"
        ouia-id="form-domain-nuova-attivazione"
        name="type"
        required
        :label="$t('Una nuova registrazione')"
      />
      <pf-radio
        :checked="domainData.new_registration === false"
        @change="domainData.new_registration = false"
        ouia-id="form-domain-da-trasferire"
        name="type"
        required
        :label="$t('Già registrato, devo trasferirlo')"
      />
    </form-group>

    <form-group
      v-if="domainData.new_registration === false"
      :name="$t('Codice authcode')"
      :help="
        $t(
          'Si tratta del codice che occorre per poter trasferire il dominio, se non lo hai devi richiederlo al tuo attuale provider che è obbligato a inviartelo.',
        )
      "
      style="max-width: 80ch"
      required
    >
      <x-input id="domainAuthcode" v-model="domainData.authcode" name="authcode" required ouia-id="authcode" />
    </form-group>

    <form-group :name="$t('Quali name server vuoi usare per il dominio?')" required>
      <p>
        {{
          $t(
            'I name server sono i server che gestiscono la zona DNS del dominio. La zona DNS gestisce il puntamento di tutti i servizi associati al dominio, tra cui sito e email. Se non hai esigenze particolari, lascia i name server di Artera: potrai gestire tutto direttamente dal tuo pannello.',
          )
        }}
      </p>
      <pf-radio
        :checked="domainData.artera_nameservers"
        @change="domainData.artera_nameservers = true"
        ouia-id="form-domain-ns-artera"
        name="artera_nameservers"
        required
        :label="$t('Name server di Artera (consigliato)')"
      />
      <pf-radio
        :checked="!domainData.artera_nameservers"
        @change="domainData.artera_nameservers = false"
        ouia-id="form-domain-ns-custom"
        name="artera_nameservers"
        required
        :label="$t('Altri name server, gestiti da me o da un altro fornitore')"
      />
    </form-group>

    <form-group v-if="!domainData.artera_nameservers" :name="$t('Name server da utilizzare')" required>
      <pf-textarea
        :model-value="domainData.nameservers"
        @update:model-value="domainData.nameservers = String($event)"
        name="nameservers"
        :rows="4"
        :placeholder="'ns1.esempio.it\nns2.esempio.it'"
        required
      />
      <template #helper-text>{{ $t('Inserisci almeno due name server, uno per riga.') }}</template>
    </form-group>

    <template v-if="domainData.artera_nameservers">
      <form-group :name="$t('Come vuoi configurare la zona DNS del dominio?')" required>
        <p>
          {{
            $t(
              'La zona DNS contiene i record (A, CNAME, MX, TXT…) che collegano il dominio al sito e alla posta. Con la zona standard di Artera il dominio funziona subito con i servizi attivi presso di noi.',
            )
          }}
        </p>
        <pf-radio
          :checked="domainData.default_zone === true"
          @change="domainData.default_zone = true"
          ouia-id="form-domain-zona-default"
          name="default_zone"
          required
          :label="$t('Zona DNS standard di Artera')"
        />
        <pf-radio
          :checked="domainData.default_zone === false"
          @change="domainData.default_zone = false"
          ouia-id="form-domain-zona-custom"
          name="default_zone"
          required
          :label="$t('Zona DNS personalizzata, con i record che indico io')"
        />
      </form-group>

      <form-group v-if="domainData.default_zone === false" :name="$t('Record della zona DNS')" required>
        <pf-textarea
          :model-value="domainData.dns_zone"
          @update:model-value="domainData.dns_zone = String($event)"
          name="dns_zone"
          :rows="6"
          :placeholder="zonePlaceholder"
          required
        />
        <template #helper-text>{{
          $t("Un record per riga con nome, tipo e valore. Puoi anche incollare l'esportazione della zona dal tuo fornitore attuale.")
        }}</template>
      </form-group>
    </template>

    <form-group :name="$t('Intestazione')">
      <div v-if="login.complete" class="billingRadio">
        <div>
          <label>
            <input type="radio" v-model="domainData.usa_dati_fatturazione" name="usa_dati_fatturazione" :value="true" required />
            {{ $t(`Intesta il dominio a ${login.azienda}`) }}
          </label>
        </div>
        <div>
          <label>
            <input type="radio" v-model="domainData.usa_dati_fatturazione" name="usa_dati_fatturazione" :value="false" required />
            {{ $t('Oppure inserisci i dati del registrante') }}
          </label>
        </div>
      </div>
    </form-group>

    <form-group :name="$t('Tipologia')" required v-if="!domainData.usa_dati_fatturazione">
      <pf-toggle-group :model-value="domainData.tipologia" @update:model-value="domainData.tipologia = $as<'Private' | 'Company'>($event)">
        <pf-toggle-group-item value="Private">{{ $t('Persona fisica') }}</pf-toggle-group-item>
        <pf-toggle-group-item value="Company">{{ $t('Azienda') }}</pf-toggle-group-item>
        <input type="text" v-model="domainData.tipologia" name="tipologia" class="input-hidden" required />
      </pf-toggle-group>
    </form-group>

    <template v-if="!domainData.usa_dati_fatturazione || login.tipologia === 'Company'">
      <form-group :name="$t('Nome')" required>
        <x-input id="domainName" v-model="domainData.nome" name="nome" required />
      </form-group>

      <form-group :name="$t('Cognome')" required>
        <x-input id="domainSurname" v-model="domainData.cognome" name="cognome" required />
      </form-group>
    </template>

    <form-group :name="$t('Email da associare alla registrazione')" required>
      <x-input id="domainEmail" v-model="domainData.email" name="email" type="email" required />
    </form-group>

    <template v-if="!domainData.usa_dati_fatturazione">
      <form-group v-if="!personaFisica" :name="$t('Ragione Sociale')" required>
        <x-input id="domainDataRagioneSociale" v-model="domainData.ragione_sociale" name="ragione_sociale" required maxlength="250" />
      </form-group>

      <form-group :name="$t('Nazione')" required>
        <pf-form-select
          id="domainDataNazione"
          :model-value="domainData.stato"
          @update:model-value="domainData.stato = String($event)"
          name="stato"
          autocomplete="country"
          required
        >
          <pf-form-select-option v-for="country in countries" :key="country.iso" :value="country.iso">
            {{ country.country }}
          </pf-form-select-option>
        </pf-form-select>
      </form-group>

      <form-group :name="$t('CAP')" required>
        <x-input id="domainDataCAP" v-model="domainData.cap" name="cap" required maxlength="10" autocomplete="postal-code" />
      </form-group>

      <form-group :name="personaFisica ? $t('Indirizzo residenza') : $t('Indirizzo sede')" required>
        <x-input id="domainDataIndirizzo" v-model="domainData.indirizzo" name="indirizzo" required maxlength="250" autocomplete="street-address" />
      </form-group>

      <form-group :name="$t('Città')" required>
        <x-input id="domainDataCitta" v-model="domainData.citta" name="citta" required maxlength="250" />
      </form-group>

      <form-group :name="domainData.stato == 'CH' ? $t('Cantone') : $t('Provincia')" required>
        <x-input id="domainDataProvincia" v-model="domainData.provincia" name="provincia" required maxlength="2" />
      </form-group>

      <form-group v-if="!personaFisica" :name="$t('Partita IVA')" :required="piCfRequired">
        <x-input id="domainDataPiva" v-model="domainData.partita_iva" name="partita_iva" :required="piCfRequired" maxlength="16" />
      </form-group>

      <form-group v-else-if="domainData.stato == 'IT'" :name="$t('Codice fiscale')" :required="piCfRequired">
        <x-input id="domainDataCF" v-model="domainData.codice_fiscale" name="codice_fiscale" :required="piCfRequired" maxlength="16" />
      </form-group>
    </template>

    <form-group v-if="domainData.new_registration === false" :name="$t('Tempistiche di trasferimento del dominio')" required>
      <p>
        {{
          $t(
            'Per procedere con il trasferimento del dominio, è necessario accordarsi sulle tempistiche, o possiamo procedere in qualsiasi momento e senza preavviso?',
          )
        }}
      </p>
      <pf-radio
        :checked="domainData.anytime === false"
        @change="domainData.anytime = false"
        name="anytime"
        ouia-id="form-domain-accordarsi"
        required
        :label="$t('Sì, dobbiamo accordarci')"
      />
      <pf-radio
        :checked="domainData.anytime === true"
        @change="domainData.anytime = true"
        name="anytime"
        ouia-id="form-domain-anytime"
        required
        :label="$t('No, procedete pure in qualsiasi momento')"
      />
    </form-group>
  </x-form>
</template>

<script lang="ts" setup>
import { type Ref, ref, computed } from 'vue';
import { type Transfer, type DomainRegistrant, DomainRegistrantResource } from '@/resources';
import { useLoginStore } from '@/store/login';
import countries from '../countries.json';

const props = defineProps<{
  transfer: Transfer;
}>();

const emit = defineEmits<{
  (e: 'submit'): void;
}>();

const login = useLoginStore();

const domainData: Ref<DomainRegistrant> = ref({
  id: props.transfer.id,
  anytime: null,
  new_registration: null,
  usa_dati_fatturazione: login.complete,
  authcode: '',
  tipologia: 'Company',
  nome: '',
  cognome: '',
  email: '',
  ragione_sociale: '',
  stato: '',
  cap: '',
  indirizzo: '',
  citta: '',
  provincia: '',
  partita_iva: '',
  codice_fiscale: '',
  artera_nameservers: true,
  nameservers: '',
  default_zone: true,
  dns_zone: '',
});

const zonePlaceholder = [
  '@    A     192.0.2.10',
  'www  CNAME esempio.it.',
  '@    MX    10 mail.esempio.it.',
  '@    TXT   "v=spf1 include:esempio.it ~all"',
].join('\n');

const personaFisica = computed(() => domainData.value.tipologia == 'Private');
const piCfRequired = computed(() => props.transfer.dominio.endsWith('.it') || domainData.value.stato == 'IT');

async function send() {
  const data = domainData.value;
  const arteraZone = data.artera_nameservers;
  await new DomainRegistrantResource().post({
    ...data,
    nameservers: arteraZone ? null : data.nameservers,
    default_zone: arteraZone ? data.default_zone : null,
    dns_zone: arteraZone && data.default_zone === false ? data.dns_zone : null,
  });
  emit('submit');
}
</script>
